import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Typography, Grid, Table, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { getOrders } from '../../http/orders';
import moment from 'moment/moment';

const OrdersTable = ({ language }) => {
  const breakpoints = Grid.useBreakpoint();
  const { t } = useTranslation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getOrders()
      .then(setOrders)
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(() => {
    return [
      {
        title: t('etc.orderId'),
        dataIndex: 'id',
        key: 'id',
        ellipsis: {
          showTitle: false
        },
        render: (_, { id }) => (
          <Typography.Text key={id} copyable code>
            {id}
          </Typography.Text>
        )
      },
      {
        title: t('etc.sum'),
        dataIndex: 'sum',
        key: 'sum',
        ellipsis: {
          showTitle: false
        },
        render: (_, { sum }) => (
          <Typography key={sum} style={{ fontWeight: 400, fontSize: 16 }}>
            {sum?.toFixed(2)}
          </Typography>
        )
      },
      {
        title: t('etc.status'),
        dataIndex: 'status',
        key: 'status',
        ellipsis: {
          showTitle: false
        },
        render: (_, { status }) => (
          <Typography.Text
            key={status}
            style={{ fontWeight: 400, fontSize: 16 }}
          >
            {status}
          </Typography.Text>
        )
      },
      {
        title: t('etc.date'),
        dataIndex: 'date',
        key: 'date',
        defaultSortOrder: 'descend',
        responsive: ['md'],
        ellipsis: {
          showTitle: false
        },
        sorter: (a, b) => new Date(a.date) - new Date(b.date),
        render: (date) => {
          let tooltipDate = null,
            innerDate = null;
          if (date) {
            tooltipDate = moment(date).format('LLL');
            innerDate = moment(date, 'YYYY-MM-DD HH:mm:ss.SSSZ').fromNow();
          }
          return (
            <Tooltip placement='left' title={tooltipDate}>
              <Typography.Text style={{ fontWeight: 400, fontSize: 16 }}>
                {innerDate}
              </Typography.Text>
            </Tooltip>
          );
        }
      }
    ];
  }, [orders, breakpoints, language]);

  const dataSource = useMemo(() => {
    return orders.map((i) => ({
      key: i.id,
      id: i.t_order_id,
      date: i.createdAt,
      status: i.status,
      sum: i.totalAmount
    }));
  }, [orders]);

  const getFiltersOptions = useCallback(() => {
    const categoryNames = [
      ...new Set(orders.map((order) => order.product.category.name))
    ];
    const categoryChildren = categoryNames.map((name) => {
      const products = orders
        .filter((order) => order.product.category.name === name)
        .map((order) => order.product.name);
      const productOptions = [...new Set(products)].map((productName) => ({
        text: productName,
        value: productName
      }));
      return {
        text: name,
        value: name,
        children: productOptions
      };
    });
    return categoryChildren;
  }, [orders]);

  return (
    <Table
      size={breakpoints.lg ? 'large' : 'small'}
      columns={columns}
      loading={loading}
      pagination={{
        position: ['none', 'bottomCenter']
      }}
      dataSource={dataSource}
    />
  );
};

export default OrdersTable;
